import { useContext, type ReactNode } from 'react';
import { ModBoss } from '@vp/mod-father';
import { PageModsContext } from '../../components/contexts/PageModsProvider';
import { modFatherTracker as hostTracker } from '../../apis/trackingApi';
import {
  commonVpDependenciesV1,
  lineItemInfoV1,
  lineItemInfoV2,
  lineItemOrderStatusV1,
  orderInfoV1,
  orderInfoV2,
  lineItemDesignInfoV1,
  lineItemShippingInfoV1,
} from '../../utils/uxModContracts';
import { useOrderSummary } from '../contexts/OrderSummaryContext';
import { useSiteData } from '../../hooks/useSiteData';
import { useLineItemData } from '../contexts/LineItemDataContext';

/**
 * This component handles injection of mods on the item level.
 * Such mods can either be from site configuration, with a 'item' scope,
 * or be attached to an order's `lineItems` objects (within an item's customOrderData.ux).
 */
export const HistoryItemUxModWrapper = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { order } = useOrderSummary();
  const item = useLineItemData();
  const { vistacartTenant } = useSiteData();
  const { activeItemLevelPageUxMods: itemUxMods } = useContext(
    PageModsContext
  ) || { activeItemLevelPageUxMods: [] };

  const attachedItemMods = item?.customOrderData?.ux || [];

  const environmentData = {
    orderInfoV1: orderInfoV1(order, vistacartTenant),
    orderInfoV2: orderInfoV2(order, vistacartTenant),
    lineItemInfoV1: lineItemInfoV1(item),
    lineItemInfoV2: lineItemInfoV2(item),
    // Commenting out cartTemplateRefV1.
    // This was looking for lineItem.cartTemplateRef which does not exist on
    // the BOP response for order history.
    // cartTemplateRefV1: cartTemplateRefV1(item),
    lineItemOrderStatusV1: lineItemOrderStatusV1(item),
    lineItemDesignInfoV1: lineItemDesignInfoV1(item),
    lineItemShippingInfoV1: lineItemShippingInfoV1(item),
    commonVpDependenciesV1,
  };

  return (
    <ModBoss
      mods={[...(itemUxMods || []), ...(attachedItemMods || [])]}
      environmentData={environmentData}
      contextKey={item.id}
      hostTracker={hostTracker}
    >
      {children}
    </ModBoss>
  );
};
